"use client";
import React, { useState, useEffect, useRef } from "react";
import { Edit, Plus, Trash2, X, ChevronsLeft, ChevronsRight, Eye, Upload, File as FileIcon } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useCertificates } from "../../context/CertificateContext";
import { CDN_URL } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";
export default function Certificates() {
  const {
  certificates,
  loading,
  fetchCertificates,
  createCertificate,
  updateCertificate,
  updateCertificateStatus,
  deleteCertificate,
} = useCertificates();
  const { canDo } = useAuth();

const loginUser = JSON.parse(localStorage.getItem("user"));

  const [expandedRows, setExpandedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalType, setModalType] = useState("create");
  const [selectedCert, setSelectedCert] = useState(null);
  // CHANGED: document is now a SINGLE File (or null) — backend only supports one document per certificate
  const [form, setForm] = useState({ name: "", document: null, status: "active" });
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  // CHANGED: previewUrl is now a single object URL (or null), not a per-index map
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Build/revoke the object URL whenever the selected file changes
  useEffect(() => {
    if (form.document instanceof File) {
      const url = URL.createObjectURL(form.document);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [form.document]);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredCertificates = certificates.filter((cert) => {
    const matchesStatus = filterStatus === "All" || cert.status === filterStatus;
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      cert.name?.toLowerCase().includes(search) ||
      cert.document?.toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredCertificates.length / rowsPerPage);
  const indexOfLastRow = page * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredCertificates.slice(indexOfFirstRow, indexOfLastRow);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Validate file type
  const isValidFileType = (file) => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];
    return validTypes.includes(file.type);
  };

  // CHANGED: Set (replace) the single selected file instead of appending to an array.
  // Backend only accepts one file under the field name "document", so only the
  // most recently chosen/dropped file is kept.
  const setSingleFile = (fileList) => {
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;

    // Only take the first file if multiple were dropped/selected
    const file = incoming[0];

    if (!isValidFileType(file)) {
      alert(
        `"${file.name}" is not a supported format. Please use PDF, DOC, DOCX, JPG, or PNG.`
      );
      return;
    }

    if (incoming.length > 1) {
      alert("Only one document is supported per certificate. The first valid file was selected.");
    }

    setForm((prev) => ({ ...prev, document: file }));
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSingleFile(e.dataTransfer.files);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSingleFile(e.target.files);
    }
    // reset input so selecting the same file again still fires onChange
    e.target.value = "";
  };

  // Check if the selected file is an image (for inline preview)
  const isImageFile = (file) => file && file.type?.startsWith("image/");

  // Human-readable file size
  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Remove the selected file
  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setForm((prev) => ({ ...prev, document: null }));
  };

  // Handle input
  const handleChange = (e) => {
    if (e.target.name === 'document') {
      handleFileChange(e);
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  // Toggle status in table (clickable capsule)
  const toggleStatus = async (cert) => {

    const newStatus =
        cert.status === "active"
            ? "inactive"
            : "active";

    const result = await updateCertificateStatus(
        cert.id,
        newStatus,
        loginUser.id
    );

};

  // Create/Edit submit
  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!form.name.trim()) {
    alert("Please enter certificate name.");
    return;
  }

  // Require the document only while creating
  if (modalType === "create" && !form.document) {
    alert("Please upload a document.");
    return;
  }

  const loginUser = JSON.parse(localStorage.getItem("user"));

  const formData = new FormData();

  formData.append("user_id", loginUser.id);
  formData.append("name", form.name);
  formData.append("status", form.status);

  // CHANGED: append the single file under "document" (singular) — this matches
  // the backend's multer field name (upload.single("document")). The previous
  // "documents" (plural) key wasn't recognized by the backend, which is why
  // the created/updated certificate always came back with document: null.
  if (form.document) {
    formData.append("document", form.document);
  }

  let result;

  if (modalType === "create") {

    formData.append("create_audit_id", loginUser.id);

    result = await createCertificate(formData);

  } else if (modalType === "edit" && selectedCert) {

    formData.append("update_audit_id", loginUser.id);

    result = await updateCertificate(
      selectedCert.id,
      formData
    );
  }

  if (result?.success) {

    setShowModal(false);

    setForm({
      name: "",
      document: null,
      status: "active",
    });

    setSelectedCert(null);

  } else {

    alert(result?.message || "Something went wrong");

  }
};

  // Delete certificate
  const handleDelete = async () => {
  const loginUser = JSON.parse(localStorage.getItem("user"));

  const result = await deleteCertificate(
    selectedCert.id,
    loginUser.id
  );

  if (result.success) {
    alert(result.message);

    setShowModal(false);
    setSelectedCert(null);
  } else {
    alert(result.message);
  }
};

  const openCreateModal = () => {
    setModalType("create");
    setForm({ name: "", document: null, status: "active" });
    setShowModal(true);
  };

  const openEditModal = (cert) => {
    setModalType("edit");
    setSelectedCert(cert);
    setForm({
      name: cert.name || "",
      document: null,
      status: cert.status || "active",
    });
    setShowModal(true);
  };

  const openDeleteModal = (cert) => {
    setModalType("delete");
    setSelectedCert(cert);
    setShowModal(true);
  };

  const openDocumentModal = (cert) => {
    setSelectedDocument(cert);
    setShowDocumentModal(true);
  };

  return (
    <>
      <div className="bg-sky-50">
        <div className="px-6 py-6 sm:px-9 max-sm:px-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Certificates</h1>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white rounded-xl px-3 py-3 shadow-sm">
            {/* Search + Filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white w-full sm:w-auto transition focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                <input
                  type="text"
                  placeholder="Search certificates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="outline-none text-sm flex-1 bg-transparent w-full"
                />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border-l border-gray-300 pl-2 text-sm outline-none bg-transparent"
                >
                  <option value="All">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {(searchTerm || filterStatus !== "All") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("All");
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
                >
                  <X size={14} />
                  Clear Filters
                </button>
              )}
            </div>

            {/* Create Button */}
                      {canDo("Certificates", "Add") && (

            <button
              onClick={openCreateModal}
              className="flex items-center justify-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto"
              style={{
                background: "linear-gradient(135deg, #2563eb, #d12465)"
              }}
            >
              <Plus size={15} />
              Add New Certificate
            </button>
                      )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full border-collapse table-fixed">
                <thead>
                  <tr className="bg-sky-100 text-gray-700">
                    <th className="py-3 px-4 text-left font-semibold w-[8%]">S.No</th>
                    <th className="py-3 px-4 text-left font-semibold w-[35%]">Name</th>
                    <th className="py-3 px-4 text-left font-semibold w-[25%]">Document</th>
                    <th className="py-3 px-4 text-left font-semibold w-[15%]">Status</th>
                                          {(canDo("Certificates", "Edit") || canDo("Certificates", "Delete") || canDo("Certificates", "View")) && (
                    <th className="py-3 px-4 text-center font-semibold w-[17%]">Actions</th>
                                          )}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length > 0 ? (
                    currentRows.map((cert, index) => {
                      return (
                      <tr
                        key={cert.id}
                        className={`border-b text-sm hover:bg-gray-50 transition ${index % 2 === 1 ? "bg-[#f7f6fe]" : ""}`}
                      >
                        <td className="py-3 px-4 text-indigo-600">
                          {indexOfFirstRow + index + 1}
                        </td>
                        <td className="py-3 px-4 text-gray-800">{cert.name}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700 text-sm truncate max-w-[200px]">
                              {cert.document || "—"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            onClick={() => toggleStatus(cert)}
                            className={`px-3 py-1 text-xs font-semibold rounded-full cursor-pointer transition-colors hover:opacity-80 ${
                              cert.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}
                          >
                            {cert.status}
                          </span>
                        </td>
                        {(canDo("Certificates", "View") ||
  canDo("Certificates", "Edit") ||
  canDo("Certificates", "Delete")) && (
  <td className="py-3 px-4 text-center">
    <div className="flex justify-center gap-3 text-gray-600">
      {canDo("Certificates", "View") && (
        <Eye
          size={16}
          className="cursor-pointer hover:text-blue-600"
          onClick={() => openDocumentModal(cert)}
          title="View Document"
        />
      )}

      {canDo("Certificates", "Edit") && (
        <Edit
          size={16}
          className="cursor-pointer hover:text-indigo-600"
          onClick={() => openEditModal(cert)}
          title="Edit Certificate"
        />
      )}

      {canDo("Certificates", "Delete") && (
        <Trash2
          size={16}
          className="cursor-pointer hover:text-red-600"
          onClick={() => openDeleteModal(cert)}
          title="Delete Certificate"
        />
      )}
    </div>
  </td>
)}
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-6 text-gray-500">
                        No certificates found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  className="border rounded-md px-2 py-1 outline-none transition focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 15].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pagination Buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft size={20} />
                </button>
                <button
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  <MdKeyboardArrowLeft size={20} />
                </button>

                {(() => {
                  let startPage = Math.max(page - 1, 1);
                  let endPage = startPage + 2;
                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(endPage - 2, 1);
                  }
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          i === page
                            ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        onClick={() => setPage(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                >
                  <MdKeyboardArrowRight size={20} />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document View Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] p-6 shadow-xl relative flex flex-col">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">
                {selectedDocument.name}
              </h3>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 bg-gray-100 rounded-lg flex items-center justify-center min-h-0 overflow-auto p-4">
              {selectedDocument.document ? (
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col items-center text-center">
                    <div className="text-4xl mb-2">📄</div>
                    <p className="text-gray-600 text-sm mb-3 truncate w-full" title={selectedDocument.document}>
                      {selectedDocument.document}
                    </p>
                    <a
                      href={`${CDN_URL}/${selectedDocument.document}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                    >
                      <Eye size={14} />
                      Open
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 text-gray-500">
                  No document attached.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit/Delete Modals */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl relative max-sm:max-w-sm max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X size={20} />
            </button>

            {modalType === "delete" ? (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Delete Certificate</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{selectedCert?.name}</span>?
                </p>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 w-full sm:w-auto"
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  {modalType === "create" ? "Add New Certificate" : "Edit Certificate"}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-700">Certificate Name</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                      placeholder="Enter certificate name"
                    />
                  </div>

                  <div>
                    <label className="text-sm text-gray-700 block mb-2">
                      Document
                    </label>
                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                        dragActive
                          ? 'border-[#8c57ff] bg-[#8c57ff]/5'
                          : form.document
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        name="document"
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                      />

                      {form.document ? (
                        <div className="flex flex-col items-center">
                          <FileIcon size={32} className="text-green-600 mb-2" />
                          <p className="text-sm font-medium text-green-700">
                            1 file selected
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Click to replace file</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Upload size={32} className="text-gray-400 mb-2" />
                          <p className="text-sm font-medium text-gray-700">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PDF, DOC, DOCX, JPG, PNG (max 10MB)
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Preview Section - single file */}
                    {form.document && (
                      <div className="mt-3">
                        <div className="border border-gray-200 rounded-lg p-3 flex items-center gap-3 bg-gray-50">
                          {isImageFile(form.document) ? (
                            <img
                              src={previewUrl}
                              alt={form.document.name}
                              className="w-14 h-14 object-cover rounded-md border border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-md border border-gray-200 bg-white flex items-center justify-center flex-shrink-0">
                              <FileIcon size={22} className="text-indigo-500" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {form.document.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(form.document.size)}
                            </p>
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1"
                            >
                              <Eye size={12} />
                              {isImageFile(form.document) ? "View full size" : "Preview file"}
                            </a>
                          </div>

                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="text-gray-400 hover:text-red-600 flex-shrink-0"
                            title="Remove file"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-700 mb-2 block">Status</label>
                    <div className="flex items-center gap-5">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="status"
                          value="active"
                          checked={form.status === "active"}
                          onChange={handleChange}
                        />
                        <span className="text-gray-700 text-sm">Active</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="status"
                          value="inactive"
                          checked={form.status === "inactive"}
                          onChange={handleChange}
                        />
                        <span className="text-gray-700 text-sm">Inactive</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
  type="submit"
  disabled={loading}
  className={`px-4 py-2 text-white rounded-lg text-sm w-full sm:w-auto flex items-center justify-center gap-2 ${
    loading ? "opacity-70 cursor-not-allowed" : ""
  }`}
  style={{
    background: "linear-gradient(135deg, #2563eb, #d12465)"
  }}
>
  {loading ? (
    <>
      <svg
        className="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
      Processing...
    </>
  ) : modalType === "create" ? (
    "Create"
  ) : (
    "Save Changes"
  )}
</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}